/**
 * Route guard + `returnTo` allowlist — TASK 7.4 Steps 2 & 3
 * (mock-defect fixes 7 and 13).
 *
 * The mock had no guard at all: `DCLogic`'s constructor defaulted
 * `flags.authed = true`, so every route rendered signed-in and the only way to
 * see the sign-in screen was to toggle a sim flag. Everything here is net-new.
 *
 * ── FIX 7 — unauthenticated visit → `/signin` (AC 1.1) ────────────────────
 * {@link guardRoute} is called by `modules/app/index.js` on EVERY navigation,
 * before the shell paints. Session truth comes from `GET /api/me` via the
 * api-client, never from a client-side flag.
 *
 * ── FIX 13 — `returnTo` is an ALLOWLIST, not a "looks relative?" test ──────
 * The classic open-redirect bug is a regex like `/^\//` — it accepts
 * `//evil.com`, `/\evil.com`, `/%09//evil.com` and friends. This module does
 * not pattern-match "relative-looking" strings at all. It:
 *
 *   1. rejects control characters (CR/LF/TAB/NUL/DEL) on the RAW input —
 *      response-splitting and filter-evasion payloads die here;
 *   2. percent-decodes (twice, defensively) and re-runs every structural check
 *      on the decoded form, so `%2f%2fevil.com` and `%09//evil.com` cannot
 *      smuggle an authority past step 3;
 *   3. requires a single leading `/`, forbids a second one (`//host` is a
 *      protocol-relative URL), and forbids `\` anywhere (browsers normalise
 *      backslashes to slashes in the authority);
 *   4. **resolves the path against `router.js#ROUTES` and requires a real,
 *      named route to match** — the catch-all `*` does NOT count. This is the
 *      allowlist the dev plan asks for: a target that is not one of the app's
 *      own declared routes can never be produced, whatever it looks like;
 *   5. re-encodes what it returns (path segments via `encodeURIComponent`,
 *      query via `URLSearchParams`), so nothing the attacker wrote survives
 *      verbatim into `location.hash`.
 *
 * Anything that fails drops to {@link HOME_ROUTE}. `apps/web/test/return-to.test.ts`
 * is table-driven over every vector in the dev plan plus the accept cases (so
 * "reject everything" cannot satisfy it).
 *
 * ── The OAuth round-trip ──────────────────────────────────────────────────
 * `GET /api/auth/google/callback` redirects to the PATH `/` on success — the
 * hash (and any `returnTo` in it) does not survive Google. So the intended
 * destination is stashed in `sessionStorage` before the redirect and consumed
 * on the way back. It is re-validated on read: same-origin storage is still
 * untrusted input (an XSS elsewhere could have written it).
 *
 * ── {@link normalizeEntryPath} — a real seam defect this fixes ────────────
 * The server redirects to the PATHS `/`, `/signin/denied` and `/signin/error`
 * (`apps/api/src/routes/auth.ts`), and `plugins/static-shell.ts` serves
 * `index.html` for them. But the client is a HASH router, so a landing on
 * `/signin/denied` with an empty hash resolves to `#/` — Home — and the denial
 * message is never shown. {@link normalizeEntryPath} promotes a known in-app
 * PATH into its hash route at boot, using the same allowlist.
 *
 * @module
 */

import { matchRoute } from '../../app/router.js';

/** Fallback for anything that fails validation. */
export const HOME_ROUTE = '/';

/** Query parameter carrying the post-sign-in destination. */
export const RETURN_TO_PARAM = 'returnTo';

/**
 * `sessionStorage` key used to carry `returnTo` across the OAuth redirect.
 * Inside the `sm:` namespace so `lib/local-purge.ts` erases it on sign-out.
 */
export const RETURN_TO_STORAGE_KEY = 'sm:returnTo';

/** Longer than any real in-app URL; a cheap DoS/obfuscation ceiling. */
export const MAX_RETURN_TO_LENGTH = 512;

/**
 * Routes that are structurally valid but must never be a post-sign-in
 * destination: bouncing back to a sign-in screen after signing in loops.
 */
const NON_RETURNABLE_ROUTE_IDS = new Set(['signin', 'signin-denied', 'signin-error']);

/**
 * True when `value` contains a C0 control, DEL, or a Unicode line separator —
 * i.e. CR/LF/TAB/NUL and friends. Written as a code-point scan rather than a
 * regex so the literal control characters never appear in source (and so
 * `no-control-regex` has nothing to complain about).
 *
 * @param {string} value
 * @returns {boolean}
 */
function hasControlChar(value) {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f || code === 0x2028 || code === 0x2029) return true;
  }
  return false;
}

/**
 * Structural checks that must hold on BOTH the raw and every decoded form.
 *
 * @param {string} value
 * @returns {boolean}
 */
function isStructurallySafe(value) {
  if (hasControlChar(value)) return false;
  // A backslash never appears in a legitimate in-app path, and browsers treat
  // it as a `/` when parsing an authority (`/\evil.com` → `//evil.com`).
  if (value.includes('\\')) return false;
  if (!value.startsWith('/')) return false;
  // Protocol-relative: `//host` and `/\/host` both resolve off-origin.
  if (value.startsWith('//')) return false;
  return true;
}

/**
 * Percent-decodes up to `passes` times, returning null when the input is not
 * valid encoding or when any intermediate form breaks the structural rules.
 *
 * @param {string} value
 * @param {number} [passes]
 * @returns {string|null}
 */
function decodeFully(value, passes = 2) {
  let current = value;
  for (let i = 0; i < passes; i += 1) {
    if (!current.includes('%')) break;
    let next;
    try {
      next = decodeURIComponent(current);
    } catch {
      return null; // malformed encoding — never guess at intent
    }
    if (next === current) break;
    current = next;
    if (!isStructurallySafe(current)) return null;
  }
  return current;
}

/**
 * @typedef {object} ResolvedInAppPath
 * @property {string} routeId matched route id from ROUTES
 * @property {string} path the decoded, validated path
 * @property {string} search re-encoded query string (no `?`), '' when absent
 * @property {string} target `path` (+ `?search`), safe to write to location.hash
 */

/**
 * Resolves a candidate against the app's own route table. Returns null unless
 * the candidate is a genuine, known in-app destination.
 *
 * @param {unknown} candidate
 * @returns {ResolvedInAppPath|null}
 */
export function resolveInAppPath(candidate) {
  if (typeof candidate !== 'string') return null;
  if (candidate === '' || candidate.length > MAX_RETURN_TO_LENGTH) return null;
  // Raw form first: an encoded control character is caught after decoding, a
  // literal one is caught here before anything else touches it.
  if (hasControlChar(candidate)) return null;

  const decoded = decodeFully(candidate);
  if (decoded === null) return null;
  if (!isStructurallySafe(decoded)) return null;
  // A `#` would splice a second hash into the URL; `@` before the first `/`
  // is userinfo. Neither can survive, and neither is legal in a route path.
  if (decoded.includes('#')) return null;

  const questionMark = decoded.indexOf('?');
  const path = questionMark < 0 ? decoded : decoded.slice(0, questionMark);
  const rawSearch = questionMark < 0 ? '' : decoded.slice(questionMark + 1);

  // THE ALLOWLIST: the path must resolve to a declared route. `matchRoute`
  // falls through to the catch-all, which is explicitly not acceptable.
  const { route } = matchRoute(path);
  if (route.id === 'not-found') return null;

  // Re-encode: nothing the caller wrote reaches `location.hash` verbatim.
  const safePath =
    path === '/'
      ? '/'
      : `/${path
          .split('/')
          .filter((segment) => segment !== '')
          .map((segment) => encodeURIComponent(segment))
          .join('/')}`;
  const search = new URLSearchParams(rawSearch).toString();

  return {
    routeId: route.id,
    path: safePath,
    search,
    target: search === '' ? safePath : `${safePath}?${search}`,
  };
}

/**
 * True when `candidate` is a legal post-sign-in destination.
 *
 * @param {unknown} candidate
 * @returns {boolean}
 */
export function isSafeReturnTo(candidate) {
  const resolved = resolveInAppPath(candidate);
  return resolved !== null && !NON_RETURNABLE_ROUTE_IDS.has(resolved.routeId);
}

/**
 * The validated destination, or {@link HOME_ROUTE} when the candidate is
 * rejected. NEVER throws — an invalid `returnTo` is a routine hostile input,
 * not an error condition.
 *
 * @param {unknown} candidate
 * @returns {string}
 */
export function sanitizeReturnTo(candidate) {
  const resolved = resolveInAppPath(candidate);
  if (resolved === null) return HOME_ROUTE;
  if (NON_RETURNABLE_ROUTE_IDS.has(resolved.routeId)) return HOME_ROUTE;
  return resolved.target;
}

/* ── sessionStorage carry-over across the OAuth redirect ───────────────── */

/**
 * @param {Window} [win]
 * @returns {Storage|null}
 */
function storageOf(win) {
  try {
    return (win ?? window).sessionStorage ?? null;
  } catch {
    return null; // blocked by a privacy setting — degrade to "no returnTo"
  }
}

/**
 * Stashes a destination for after the Google round-trip. Validated on the way
 * IN as well as out, so an invalid value never even reaches storage.
 *
 * @param {unknown} candidate
 * @param {Window} [win]
 * @returns {boolean} whether anything was stashed
 */
export function stashReturnTo(candidate, win) {
  if (!isSafeReturnTo(candidate)) return false;
  const store = storageOf(win);
  if (store === null) return false;
  try {
    store.setItem(RETURN_TO_STORAGE_KEY, sanitizeReturnTo(candidate));
    return true;
  } catch {
    return false;
  }
}

/**
 * Reads and clears the stashed destination, re-validating it (storage is
 * untrusted input). Returns null when there is nothing valid to return to.
 *
 * @param {Window} [win]
 * @returns {string|null}
 */
export function consumeReturnTo(win) {
  const store = storageOf(win);
  if (store === null) return null;
  let raw;
  try {
    raw = store.getItem(RETURN_TO_STORAGE_KEY);
    store.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  return isSafeReturnTo(raw) ? sanitizeReturnTo(raw) : null;
}

/* ── The guard ─────────────────────────────────────────────────────────── */

/**
 * @typedef {object} GuardDecision
 * @property {string|null} redirectTo hash to navigate to instead, or null to
 *   render the requested route
 * @property {string|null} returnTo the destination captured for after sign-in
 */

/**
 * Decides whether a navigation may proceed.
 *
 * @param {{
 *   route: { id: string, shell: boolean },
 *   hash: string,
 *   session: { authenticated: boolean },
 *   win?: Window,
 * }} args
 * @returns {GuardDecision}
 */
export function guardRoute(args) {
  const { route, hash, session, win } = args;
  const authenticated = session.authenticated === true;

  // The sign-in family is the only thing an unauthenticated visitor may see.
  if (route.shell === false) {
    if (!authenticated) return { redirectTo: null, returnTo: null };
    // Already signed in: never sit on a sign-in screen. Land on the stashed
    // destination if there is one, else Home.
    return { redirectTo: `#${consumeReturnTo(win) ?? HOME_ROUTE}`, returnTo: null };
  }

  if (authenticated) return { redirectTo: null, returnTo: null };

  // AC 1.1 — unauthenticated visit to ANY route goes to sign-in, remembering
  // where the user was headed (validated before it is written anywhere).
  const returnTo = isSafeReturnTo(hash) ? sanitizeReturnTo(hash) : null;
  if (returnTo !== null) stashReturnTo(returnTo, win);
  const query = returnTo === null ? '' : `?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`;
  return { redirectTo: `#/signin${query}`, returnTo };
}

/**
 * Promotes a PATH deep link (`/signin/denied`, `/queue`, …) into its hash
 * route at boot. See the module header: without this, every server-side
 * redirect target and every SPA deep link lands on Home.
 *
 * @param {Window} [win]
 * @returns {string|null} the hash it installed, or null when it did nothing
 */
export function normalizeEntryPath(win = window) {
  const { pathname, search, hash } = win.location;
  // A hash already present wins — it is the router's own address space.
  if (hash !== '' && hash !== '#') return null;
  if (pathname === '' || pathname === '/') return null;

  const resolved = resolveInAppPath(`${pathname}${search ?? ''}`);
  if (resolved === null) return null;

  const next = `#${resolved.target}`;
  // replaceState, not assignment: this is a correction of the entry URL, not a
  // navigation, and it must not leave a bogus back-stack entry.
  win.history.replaceState(null, '', `/${next}`);
  return next;
}
